import Slider from '@material-ui/core/Slider';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import styled from 'styled-components';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle';
import { useGetDeviceAlertRules } from '../../../hooks/useGetDeviceAlertRules';
import screenNavigator from '../../../services/ScreenNavigator';
import { customFetchV2 } from '../../../services/custom-fetch-v2';
import Button from '../../bits/Button.jsx';
import { SlideComponent } from '../../bits/SlideComponent';
import Switch from '../../bits/Switch.jsx';
import Tile from '../../bits/Tile.jsx';
import TileActionRow from '../../bits/TileActionRow.jsx';
import Loading from '../Loading.jsx';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const ActionsContainer = styled.div`
  margin-top: 30px;
`;

const SpeedControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 20px;
`;

const SpeedLabels = styled.div`
  text-align: center;
  font-size: 48px;
`;

const SpeedLabelUnit = styled.span`
  font-size: 16px;
  margin-left: 6px;
`;

const SPEEDING_ALERT_RULE_TYPE = 'DeviceAlertRules::SpeedingAlertRule';

interface EditSpeedAlertRouteProps {};

interface SpeedControlsProps {
  speed: number;
  onSpeedChange: (event: React.ChangeEvent<{}>, value: number | number[]) => void;
}

const SpeedControls: React.FC<SpeedControlsProps> = ({ speed, onSpeedChange }: SpeedControlsProps) => {
  return (
    <SpeedControlsContainer>
      <SpeedLabels>
        {speed}
        <SpeedLabelUnit>mph</SpeedLabelUnit>
      </SpeedLabels>
      <Slider 
        style={{ width: '88%', color: '#4a8bcc' }}
        value={speed} 
        onChange={onSpeedChange} 
        aria-labelledby="continuous-slider" />
    </SpeedControlsContainer>
  );
};

export const EditSpeedAlertRoute: React.FC<EditSpeedAlertRouteProps> = () => {
  useSetModalTitle('Speed Alert');

  const { id: deviceId } = useParams<{ id: string }>();
  const history = useHistory();

  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [speedValue, setSpeedValue] = useState<number>(0);
  const [alertEnabled, setAlertEnabled] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);

  const { data: alertRules, isLoading } = useGetDeviceAlertRules({ deviceId });
  
  useEffect(() => {
    if (!alertRules) {
      return;
    }

    const speedAlertRule = alertRules.find(rule => rule.ruleId === SPEEDING_ALERT_RULE_TYPE) || null;

    if (speedAlertRule) {
      setAlertEnabled(speedAlertRule.enabled);
      setSpeedValue(speedAlertRule.params.speed);
    }
  }, [alertRules]);

  /**
   * Save the speed alert settings.
   */
  const onSave = useCallback(async () => {
      setIsSaving(true);
  
      const payload = {
        type: SPEEDING_ALERT_RULE_TYPE,
        enabled: alertEnabled,
        params: {
          speed: speedValue
        },
      };
  
      try {
        const response = await customFetchV2(`/api/v2/devices/${deviceId}/alert-rules/${SPEEDING_ALERT_RULE_TYPE}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
  
        if (!response.ok) {
          setErrorMessage('Failed to save speed alert settings');
          return;
        };
  
        screenNavigator.exitCurrentScreen(history);
      } catch (error) {
        setErrorMessage('Failed to save speed alert settings');
      } finally {
        setIsSaving(false);
      }
  }, [deviceId, alertEnabled, speedValue]);

  /** Handler for rule enable switch */
  const onEnabledToggle = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setAlertEnabled(e.target.checked);
    setHasChanged(true);
  }, []);

  /** Handler for speed change slider */
  const onSpeedChange = useCallback((event: React.ChangeEvent<{}>, value: number | number[]) => {
    setSpeedValue(value as number);
    setHasChanged(true);
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Container>
      <Tile>
        <TileActionRow label="Enabled" action={
          <Switch
            name="alertEnabled"
            className="up-8"
            edge="end"
            onChange={onEnabledToggle}
            checked={alertEnabled}
            inputProps={{ 'aria-labelledby': 'switch-list-label-bluetooth' }}
          />} />
      </Tile>

      <SlideComponent in={alertEnabled}>
        <SpeedControls speed={speedValue} onSpeedChange={onSpeedChange} />
      </SlideComponent>

      <ActionsContainer>
        <Button
          onClick={onSave}
          color="primary"
          disabled={!hasChanged}
          loading={isSaving}>Save</Button>
      </ActionsContainer>
    </Container>
  );
};

export default EditSpeedAlertRoute;
